import { TeamBand } from "@/components/TeamBand";
import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin, CheckCircle2, Copy } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";

export default function Contact() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const produit = params.get("produit");
  const formation = params.get("formation");
  const st = loc.state || {};

  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    subject: st.subject || (produit ? `Devis : ${produit}` : formation ? `Inscription session : ${formation}` : ""),
    message: st.message || (produit ? `Bonjour, je souhaite un devis pour : ${produit}.` : formation ? `Bonjour, je souhaite inscrire un ou plusieurs participants à la prochaine session « ${formation} ». Merci de me communiquer les modalités.` : ""),
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(sent.id);
      toast.success("Référence copiée !");
    } catch {
      toast.info("Copie automatique indisponible — sélectionnez la référence manuellement.");
    }
  };

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/quotes", form);
      toast.success("Demande envoyée ! Réponse sous 48h.");
      setSent(data);
      setForm({ name: "", company: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-[148px]">
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Contact</p></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tight leading-none">Demander<br />un devis</h1>
            <p className="mt-6 text-lg" style={{ color: "var(--ink-2)" }}>
              Décrivez votre besoin, notre équipe vous répond sous 48h avec une proposition personnalisée.
            </p>
            <div className="mt-10 space-y-4">
              <a href="mailto:contact@aldol-solutions.fr" className="link-hover flex items-center gap-3" data-testid="contact-email-link"><Mail size={18} style={{ color: "var(--orange)" }} /> contact@aldol-solutions.fr</a>
              <a href="tel:+33780816014" className="link-hover flex items-center gap-3" data-testid="contact-phone-link"><Phone size={18} style={{ color: "var(--orange)" }} /> 07 80 81 60 14</a>
              <p className="flex items-start gap-3"><MapPin size={18} className="mt-1 shrink-0" style={{ color: "var(--orange)" }} /> 15 Avenue de l'Alma, 94210 Saint-Maur-des-Fossés, France</p>
            </div>
            <div className="mt-10" style={{ border: "1px solid var(--border)" }} data-testid="contact-map">
              <iframe
                title="ALDOL-SOLUTIONS — 15 Avenue de l'Alma, Saint-Maur-des-Fossés"
                src="https://maps.google.com/maps?q=15%20Avenue%20de%20l%27Alma%2094210%20Saint-Maur-des-Foss%C3%A9s%20France&z=15&output=embed"
                className="w-full"
                style={{ height: "300px", border: 0, display: "block" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            {sent ? (
              <div className="p-8 md:p-10 text-center" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="quote-sent-confirmation">
                <CheckCircle2 className="mx-auto" size={52} style={{ color: "var(--orange)" }} />
                <h2 className="font-display font-black text-3xl mt-6">Demande envoyée !</h2>
                <p className="mt-4 text-lg" style={{ color: "var(--ink-2)" }}>
                  Notre équipe vous répond sous 48h ouvrées. Conservez votre référence de suivi :
                </p>
                <div className="mt-6 flex items-center justify-center gap-3 p-4 font-mono2 text-sm break-all"
                  style={{ background: "var(--bone)", border: "1px dashed var(--orange)" }} data-testid="quote-ref">
                  {sent.id}
                  <button onClick={copyRef}
                    className="shrink-0 p-1" data-testid="quote-ref-copy" aria-label="Copier la référence">
                    <Copy size={16} style={{ color: "var(--orange)" }} />
                  </button>
                </div>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to={`/suivi-devis?ref=${sent.id}`} className="btn-primary px-8 py-4 font-semibold" data-testid="quote-track-link">
                    Suivre ma demande
                  </Link>
                  <button onClick={() => setSent(null)} className="btn-outline px-8 py-4 font-semibold" data-testid="quote-new-btn">
                    Nouvelle demande
                  </button>
                </div>
              </div>
            ) : (
            <form onSubmit={submit} className="p-8 md:p-10 space-y-4" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="quote-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input className="field" placeholder="Nom complet *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quote-name" />
                <input className="field" placeholder="Entreprise" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="quote-company" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input className="field" type="email" placeholder="Email *" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quote-email" />
                <input className="field" placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="quote-phone" />
              </div>
              <input className="field" placeholder="Objet *" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="quote-subject" />
              <textarea className="field" rows={5} placeholder="Votre message *" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="quote-message" />
              <button type="submit" disabled={loading} className="btn-primary w-full py-4 font-semibold" data-testid="quote-submit">
                {loading ? "Envoi en cours..." : "Envoyer ma demande"}
              </button>
              <p className="text-xs text-center" style={{ color: "var(--ink-2)" }}>
                Déjà une demande en cours ? <Link to="/suivi-devis" className="font-semibold link-hover" style={{ color: "var(--orange)" }} data-testid="quote-track-existing-link">Suivre ma demande</Link>
              </p>
            </form>
            )}
          </Reveal>
        </div>
      </section>
      <TeamBand compact />
    </div>
  );
}
